import * as React from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { ConfirmationModal, useL2DTheme, useModal } from '@europrocurement/flexy-components';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import {
    type ApiDossiersIdActionPutActionEnum,
    type ApiDossiersIdTransitionsGetCollection200ResponseHydraMemberInner,
    type StatutJsonldStatutRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { useSnackbar } from 'notistack';
import useTransition from '../../../hooks/useTransition';
import {
    REFRESH_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
} from '../../../providers/CurrentDossierProvider';
import StatusChip from './StatusChip';
import TimelineHaloWithDot from './TimelineHaloWithDot';
import {
    type TimelineColorContext,
    getHaloColor,
    getDotColor,
    getConnectorColor,
    getDesktopAlignment,
} from './timelineColors';

export type TimelineWithValidatorsStepProps = {
    id: number;
    label: string;
    icon?: IconName;
    current: boolean;
    onClick?: () => void;
};

export type TimelineWithValidatorsProps = {
    steps: Array<TimelineWithValidatorsStepProps>;
    showAllStatus?: boolean;
};

// Sizing constants — change once to resize dots/connectors everywhere
const HALO_SIZE = 20;
const DOT_SIZE = 12;
const CONNECTOR_THICKNESS = 2;

const TimeLineWithValidators: React.FunctionComponent<TimelineWithValidatorsProps> = function ({
    steps,
    showAllStatus = true,
}) {
    const { palette, breakpoints } = useL2DTheme();
    const isMobile = useMediaQuery(breakpoints.down('md'));
    const { statusApi } = useFormaliteService();
    const { dossier } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();
    const { applyTransition, canApplyTransition, getTransitionStatusAvailable } = useTransition();
    const { enqueueSnackbar } = useSnackbar();
    const { modalActions } = useModal();

    const [allStatuses, setAllStatuses] = React.useState<StatutJsonldStatutRead[]>([]);
    const [availableTransitions, setAvailableTransitions] = React.useState<
        ApiDossiersIdTransitionsGetCollection200ResponseHydraMemberInner[]
    >([]);
    const [isApplying, setIsApplying] = React.useState<number | null>(null);

    React.useEffect(() => {
        statusApi
            .apiStatutsGetCollection({ itemsPerPage: 50, type: 1 })
            .then((res) =>
                setAllStatuses(res.data['hydra:member'].filter((status) => status.step) || []),
            )
            .catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (!dossier) return;
        getTransitionStatusAvailable(dossier)
            .then((res) => setAvailableTransitions(res['hydra:member'] || []))
            .catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier?.statutId]);

    const findTransitionForStatus = React.useCallback(
        (statusId: number) => availableTransitions.find((t) => t.target?.id === statusId),
        [availableTransitions],
    );

    const handleStatusClick = React.useCallback(
        async (statusId: number, identifier: string) => {
            if (!dossier) return;
            const transition = identifier as ApiDossiersIdActionPutActionEnum;
            if (!canApplyTransition(dossier, transition)) return;
            setIsApplying(statusId);
            try {
                await applyTransition(dossier, transition);
                enqueueSnackbar('Statut mis à jour', { variant: 'success' });
                dispatch?.({ type: REFRESH_CURRENT_DOSSIER_CTX });
            } catch {
                enqueueSnackbar('Erreur lors de la mise à jour du statut', { variant: 'error' });
            } finally {
                setIsApplying(null);
            }
        },
        [dossier, canApplyTransition, applyTransition, dispatch, enqueueSnackbar],
    );

    // ── Timeline geometry ────────────────────────────────────────────────────

    let currentDone = false;
    const currentIndex = steps.reduce((acc, step) => {
        currentDone = currentDone || step.current;
        return !currentDone || step.current ? acc + 1 : acc;
    }, -1);

    // ── Color context (passed to pure helper functions) ──────────────────────

    const inactiveColor = palette.mode === 'dark' ? palette.grey[600] : palette.grey[300];

    const colorCtx: TimelineColorContext = {
        isDark: palette.mode === 'dark',
        primaryMain: palette.primary.main,
        greyLight: palette.grey[200],
        greyDark: palette.grey[700],
        inactiveColor,
    };

    // ── Sx groups ────────────────────────────────────────────────────────────

    // Outer container
    const wrapperSx = {
        width: '100%',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
    } as const;

    // Desktop step: flex column, equal width, content aligned by position
    const desktopStepSx = (index: number, clickable: boolean) => ({
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: getDesktopAlignment(index, steps.length),
        flexGrow: 1,
        gap: '8px',
        cursor: clickable ? 'pointer' : 'auto',
        color: palette.grey[500],
        '&:hover': { color: clickable ? palette.primary.main : palette.grey[500] },
    });

    // Desktop dot row: [left half-connector] [halo] [right half-connector]
    const dotRowSx = {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
    };

    // Horizontal connector half
    const horizontalConnectorSx = (color: string) => ({
        flex: 1,
        height: `${CONNECTOR_THICKNESS}px`,
        backgroundColor: color,
        borderRadius: '1px',
    });

    // Mobile step: full-width row, connector column on the left
    const mobileStepSx = (clickable: boolean) => ({
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'stretch',
        width: '100%',
        cursor: clickable ? 'pointer' : 'auto',
        color: palette.grey[500],
        '&:hover': { color: clickable ? palette.primary.main : palette.grey[500] },
    });

    // Mobile connector column: [top segment] [halo] [bottom segment]
    const mobileConnectorColumnSx = {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        width: `${HALO_SIZE}px`,
    };

    // Vertical connector segment
    const verticalConnectorSx = (color: string, visible: boolean) => ({
        flex: 1,
        width: `${CONNECTOR_THICKNESS}px`,
        minHeight: '12px',
        backgroundColor: color,
        borderRadius: '1px',
        visibility: visible ? ('visible' as const) : ('hidden' as const),
    });

    // Step label
    const labelSx = (clickable: boolean) => ({
        fontWeight: 500,
        fontSize: isMobile ? '14px' : '12px',
        lineHeight: '14.4px',
        cursor: clickable ? 'pointer' : 'auto',
        transition: 'color 0.225s ease-out',
    });

    // Mobile badge: status count displayed inline next to the label
    const mobileBadgeSx = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '16px',
        height: '16px',
        px: '4px',
        ml: '4px',
        borderRadius: '8px',
        backgroundColor: palette.primary.main,
        color: palette.background.paper,
        fontSize: '10px',
        fontWeight: 600,
        lineHeight: 1,
    };

    // Desktop chips list
    const chipsSectionSx = {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '4px',
    };

    // ── Render helpers ───────────────────────────────────────────────────────

    const renderHalo = (index: number, icon?: IconName) => (
        <TimelineHaloWithDot
            haloColor={getHaloColor(index, currentIndex, colorCtx)}
            dotColor={getDotColor(index, currentIndex, colorCtx)}
            iconColor={palette.background.paper}
            icon={icon}
            haloSize={HALO_SIZE}
            dotSize={DOT_SIZE}
        />
    );

    const renderChips = (visibleStatuses: StatutJsonldStatutRead[]) => (
        <Box
            sx={chipsSectionSx}
            onClick={(e) => e.stopPropagation()}
        >
            {visibleStatuses.map((status) => {
                const transition = findTransitionForStatus(status.id as number);
                return (
                    <StatusChip
                        key={status.id}
                        status={status}
                        isCurrent={status.id === dossier?.statutId}
                        isClickable={!!transition}
                        isProcessing={isApplying === status.id}
                        onTransitionClick={
                            transition
                                ? () => {
                                      modalActions.call(
                                          <ConfirmationModal
                                              isFullWidth
                                              color={palette.primary}
                                              messages={{
                                                  question:
                                                      'Souhaitez-vous modifier le statut de ce dossier ?',
                                                  detail: {
                                                      message:
                                                          'La modification du statut impactera la progression du dossier.',
                                                      severity: 'info',
                                                  },
                                                  validation: 'Confirmer la modification',
                                              }}
                                              actionOnValidation={() => {
                                                  handleStatusClick(
                                                      status.id as number,
                                                      transition.identifier as string,
                                                  );
                                                  modalActions.close();
                                              }}
                                              actionOnCancellation={() => modalActions.close()}
                                          />,
                                      );
                                  }
                                : undefined
                        }
                    />
                );
            })}
        </Box>
    );

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={wrapperSx}
                data-testid="timeline-with-validators"
            >
                {steps.map((step, index) => {
                    const isFirst = index === 0;
                    const isLast = index === steps.length - 1;
                    const clickable = !!step.onClick;

                    // Left segment = done when the current step is here or past it
                    const leftColor = getConnectorColor(currentIndex >= index, colorCtx);
                    // Right segment = done only when this step is already behind us
                    const rightColor = getConnectorColor(currentIndex > index, colorCtx);

                    const visibleStatuses = allStatuses
                        .filter((s) => s.step!.id === step.id)
                        .filter((status) =>
                            showAllStatus
                                ? true
                                : !!findTransitionForStatus(status.id as number) ||
                                  status.id === dossier?.statutId,
                        );

                    if (isMobile) {
                        return (
                            <Box
                                key={`step-${step.id}`}
                                sx={mobileStepSx(clickable)}
                                onClick={step.onClick}
                            >
                                {/* Left column: top line → halo+dot → bottom line */}
                                <Box sx={mobileConnectorColumnSx}>
                                    <Box sx={verticalConnectorSx(leftColor, !isFirst)} />
                                    {renderHalo(index, step.icon)}
                                    <Box sx={verticalConnectorSx(rightColor, !isLast)} />
                                </Box>

                                {/* Content: label + status count badge */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        ml: 1.5,
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        sx={labelSx(clickable)}
                                    >
                                        {step.label}
                                    </Typography>
                                    {visibleStatuses.length > 0 && (
                                        <Typography
                                            component="span"
                                            sx={mobileBadgeSx}
                                        >
                                            {visibleStatuses.length}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    }

                    return (
                        <Box
                            key={`step-${step.id}`}
                            sx={desktopStepSx(index, clickable)}
                            onClick={step.onClick}
                        >
                            {/* Dot row: [left half] [halo+dot] [right half]
                                Half-connectors use flex:1 → dot flush on first/last,
                                centered between connectors on all other steps. */}
                            <Box sx={dotRowSx}>
                                {!isFirst && <Box sx={horizontalConnectorSx(leftColor)} />}
                                {renderHalo(index, step.icon)}
                                {!isLast && <Box sx={horizontalConnectorSx(rightColor)} />}
                            </Box>

                            {/* Label */}
                            <Typography
                                component="span"
                                sx={labelSx(clickable)}
                            >
                                {step.label}
                            </Typography>

                            {/* Status chips */}
                            {visibleStatuses.length > 0 && renderChips(visibleStatuses)}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default TimeLineWithValidators;
